import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import {
  ProgressSectionId,
} from "../../lib/progress-calculator";
import { getNamespaceTranslations } from "../../lib/questionnaire-translations";

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  sectionIndex: number;
  totalSections: number;
  sectionId: ProgressSectionId;
  language?: "en" | "es";
}

export function ProgressBar({
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  language = "en",
}: ProgressBarProps) {
  const progressT = getNamespaceTranslations(language, "progress");
  const percentage = Math.min(
    totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0,
    100
  );
  const displayPercentage = Math.round(percentage);

  const sectionName = progressT?.sections?.[sectionId] ?? sectionId;
  const stepLabel = (progressT?.stepOf ?? "Step {{current}} of {{total}}")
    .replace("{{current}}", String(sectionIndex))
    .replace("{{total}}", String(totalSections));

  const [showMilestone, setShowMilestone] = useState(false);
  const prevSectionRef = useRef(sectionIndex);

  useEffect(() => {
    if (sectionIndex > prevSectionRef.current) {
      setShowMilestone(true);
      const timer = window.setTimeout(() => setShowMilestone(false), 1800);
      prevSectionRef.current = sectionIndex;
      return () => window.clearTimeout(timer);
    }
    prevSectionRef.current = sectionIndex;
  }, [sectionIndex]);

  return (
    <div className="w-full" role="group" aria-label={stepLabel}>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <p
          className="text-sm leading-5 font-semibold text-[#1A1A1A] m-0"
          style={{ fontFamily: "TT Commons Pro" }}
        >
          {stepLabel}
          <span className="font-semibold"> · {sectionName}</span>
        </p>
        <p
          className="text-sm leading-5 font-medium text-[#9E9E9E] m-0 shrink-0"
          style={{ fontFamily: "TT Commons Pro" }}
          aria-live="polite"
        >
          {displayPercentage}%
        </p>
      </div>

      {/* Continuous journey progress — never resets between sections */}
      <div
        className="w-full h-[5px] min-h-[5px] bg-[#E0E0E0] rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={displayPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${displayPercentage}%`}
      >
        <div
          className="h-full bg-[#3CC34E] rounded-full transition-all duration-300 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {showMilestone && progressT?.sectionComplete && (
        <div
          className="flex items-center gap-1.5 mt-2 text-[#3CC34E] animate-fade-in"
          role="status"
        >
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#3CC34E] text-white">
            <Check className="w-2.5 h-2.5" strokeWidth={3} />
          </span>
          <span
            className="text-sm font-medium"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {progressT.sectionComplete}
          </span>
        </div>
      )}
    </div>
  );
}
